import React from 'react';

// List of images for the carousel
const defaultImages: string[] = [
  'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1560472355-536de3962603?q=80&w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=400&auto=format&fit=crop',
];

/**
 * Props for the VerticalImageCarousel component.
 */
interface VerticalImageCarouselProps {
  /** Array of image URLs. Defaults to placeholder images. */
  images?: string[];
  /** Height of each individual image in pixels. Defaults to 200. */
  imageHeight?: number;
  /** Width of the carousel container in pixels. Defaults to 300. */
  carouselWidth?: number;
  /** Margin between images in pixels. Defaults to 20. */
  marginBetweenImages?: number;
  /** Duration of one full scroll cycle in seconds. Defaults to 25. */
  scrollDuration?: number;
}

/**
 * VerticalImageCarousel component
 * Displays a continuously scrolling vertical list of images with a fade effect at the top and bottom.
 */
const VerticalImageCarousel: React.FC<VerticalImageCarouselProps> = ({
  images = defaultImages,
  imageHeight = 200,
  carouselWidth = 800,
  marginBetweenImages = 20,
  scrollDuration = 25,
}) => {
  // Duplicate images for seamless looping effect
  const extendedImages: string[] = [...images, ...images, ...images];

  // Calculate the total height of one set of images plus gaps
  const totalScrollDistance = images.length * (imageHeight + marginBetweenImages);

  // Create CSS animation keyframes dynamically
  const animationStyle = {
    animation: `scrollVertical ${scrollDuration}s linear infinite`,
  };

  return (
    <>
      <style>
        {`
          @keyframes scrollVertical {
            0% {
              transform: translateY(0px);
            }
            100% {
              transform: translateY(-${totalScrollDistance}px);
            }
          }
          
          .carousel-container {
            mask-image: linear-gradient(to bottom, 
              transparent 0%, 
              rgba(0,0,0,0.1) 5%, 
              rgba(0,0,0,0.5) 15%, 
              black 25%, 
              black 75%, 
              rgba(0,0,0,0.5) 85%, 
              rgba(0,0,0,0.1) 95%, 
              transparent 100%
            );
            -webkit-mask-image: linear-gradient(to bottom, 
              transparent 0%, 
              rgba(0,0,0,0.1) 5%, 
              rgba(0,0,0,0.5) 15%, 
              black 25%, 
              black 75%, 
              rgba(0,0,0,0.5) 85%, 
              rgba(0,0,0,0.1) 95%, 
              transparent 100%
            );
          }
          
          .fade-container {
            position: relative;
          }
          
          .fade-container::before,
          .fade-container::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            height: 80px;
            pointer-events: none;
            z-index: 10;
          }
          
          .fade-container::before {
            top: 0;
            background: linear-gradient(to bottom, 
              rgba(255,255,255,0.8) 0%, 
              rgba(255,255,255,0.4) 40%, 
              transparent 100%
            );
          }
          
          .fade-container::after {
            bottom: 0;
            background: linear-gradient(to top, 
              rgba(255,255,255,0.8) 0%, 
              rgba(255,255,255,0.4) 40%, 
              transparent 100%
            );
          }
          

        `}
      </style>
      
      <div className="w-full mx-auto" style={{ maxWidth: `${carouselWidth}px` }}>
        {/* Carousel container with fade effect */}
        <div className="h-[600px] w-full overflow-hidden carousel-container fade-container relative">
          {/* Animated container */}
          <div
            className="flex flex-col"
            style={animationStyle}
          >
            {extendedImages.map((src: string, index: number) => (
              <div 
                key={index} 
                className="shadow-md bg-gray-100 overflow-hidden"
                style={{ 
                  height: `${imageHeight}px`,
                  width: '100%',
                  marginBottom: `${marginBetweenImages}px`,
                  borderRadius: '16px',
                }}
              >
                <img 
                  src={src} 
                  alt={`Carousel item ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '16px',
                    display: 'block',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default VerticalImageCarousel;